import {
  Radio as HeadlessRadio,
  type RadioProps as HeadlessRadioProps,
} from "@headlessui/react";
import classNames from "classnames";
import { type ElementType, useContext } from "react";

import { FieldContext } from "./FieldContext";

const sizes = {
  sm: "size-4",
  md: "size-5",
};

const dotSizes = {
  sm: "size-1.5",
  md: "size-2",
};

export type RadioProps<T extends ElementType = "span", TType = string> = {
  size?: keyof typeof sizes;
} & HeadlessRadioProps<T, TType>;

export default function Radio<TType = string>({
  className,
  size = "md",
  ...props
}: RadioProps<"span", TType>) {
  const fieldContext = useContext(FieldContext);

  return (
    <HeadlessRadio
      {...props}
      className={({ hover, focus, checked }) =>
        classNames(
          "group ring-primary-100 active:border-primary-300 active:bg-primary-50 flex shrink-0 items-center justify-center rounded-full border active:ring-4 data-disabled:bg-gray-100",
          checked
            ? "border-primary-600 bg-primary-50 data-disabled:border-gray-300"
            : "border-gray-300",

          hover && !focus && "border-primary-600 bg-primary-100",

          focus && "border-primary-300 ring-4",

          fieldContext.grid && "sm:col-span-2",
          sizes[size],
          className,
        )
      }
    >
      <div
        className={classNames(
          "bg-primary-600 hidden rounded-full group-data-checked:block group-data-disabled:bg-gray-300",
          dotSizes[size],
        )}
      />
    </HeadlessRadio>
  );
}
